import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Badge, Avatar } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  margin-top: 40px;
  padding: 0 16px;
  transition: background 0.3s ease;
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const StyledButton = styled(Button)`
  background-color: #2D6CCA !important;
  color: white !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  border: none !important;
  height: 40px;
  padding: 0 24px;
  
  &:hover {
    background-color: #2560b8 !important;
    color: white !important;
  }
`;

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const StyledCard = styled(Card)`
  background: ${(props) => getThemeValue(props, '#2D3748', '#ffffff')};
  border-color: ${(props) => getThemeValue(props, '#4a5568', '#d9d9d9')};
  border-radius: 12px;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
      )};
  }

  .ant-card-body {
    padding: 24px;
    background: transparent;
  }
`;

export const CardTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  transition: color 0.3s ease;
`;

export const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const DeviceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SessionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#374151', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${(props) => getThemeValue(props, '#4B5563', '#f9fafb')};
    border-color: ${(props) => getThemeValue(props, '#2D6CCA', '#2D6CCA')};
  }
`;

export const DeviceItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#374151', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${(props) => getThemeValue(props, '#4B5563', '#f9fafb')};
    border-color: ${(props) => getThemeValue(props, '#2D6CCA', '#2D6CCA')};
  }
`;

export const StyledAvatar = styled(Avatar)`
  margin-right: 16px;
  background: ${(props) => getThemeValue(props, '#2D6CCA', '#2D6CCA')};
  color: white;
  flex-shrink: 0;
`;

export const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ItemTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  margin-bottom: 4px;
  transition: color 0.3s ease;
`;

export const ItemSubtitle = styled.div`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')};
  margin-bottom: 8px;
  transition: color 0.3s ease;
`;

export const StyledBadge = styled(Badge)`
  .ant-badge-status-dot {
    width: 8px;
    height: 8px;
  }
`;

export const ErrorContainer = styled.div`
  margin-top: 40px;
  text-align: center;
  padding: 40px 16px;
`;

export const ErrorText = styled.div`
  color: #ff4d4f;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 16px;
`;

export const LoadingContainer = styled.div`
  margin-top: 40px;
  text-align: center;
  padding: 40px 16px;
`;

export const LoadingText = styled.div`
  margin-top: 16px;
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

export const EmptyStateIcon = styled.div`
  font-size: 64px;
  color: ${(props) => getThemeValue(props, '#718096', '#A0AEC0')};
  opacity: 0.6;
`;

export const EmptyStateText = styled.div`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')};
  font-size: 14px;
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => getThemeValue(props, '#2D6CCA', '#2D6CCA')};
  color: white;
  margin-right: 16px;
  flex-shrink: 0;

  svg {
    font-size: 18px;
  }
`;
